import { HTTPError } from "ky";

import { listGuestUsers } from "@/features/users/api/admin-users";
import type {
	GuestUserRow,
	ListGuestUsersParams,
} from "@/features/users/api/types";
import type { InfinityPage, PageLimit } from "@/shared/api/types";

const SCAN_LIMIT: PageLimit = 100;
const SCAN_PAGES = 25;

function rejectedByApi(error: unknown): boolean {
	return (
		error instanceof HTTPError &&
		(error.response.status === 400 || error.response.status === 422)
	);
}

function safeParams(params: ListGuestUsersParams): ListGuestUsersParams {
	return {
		...params,
		sort: params.sort === "deletionRequested" ? "createdAt" : params.sort,
		deletionRequested: undefined,
	};
}

function serverHonouredFilter(page: InfinityPage<GuestUserRow>): boolean {
	return page.data.every((row) => Boolean(row.deletionRequestedAt));
}

function byDeletionRequested(order: ListGuestUsersParams["order"]) {
	const direction = order === "ASC" ? 1 : -1;
	return (left: GuestUserRow, right: GuestUserRow) => {
		const earlier = left.deletionRequestedAt ?? "";
		const later = right.deletionRequestedAt ?? "";
		if (!earlier && !later) {
			return 0;
		}
		if (!earlier) {
			return 1;
		}
		if (!later) {
			return -1;
		}
		if (earlier === later) {
			return 0;
		}
		return earlier < later ? -direction : direction;
	};
}

function slicePage(
	rows: GuestUserRow[],
	params: ListGuestUsersParams,
): InfinityPage<GuestUserRow> {
	const limit = params.limit ?? 20;
	const page = params.page ?? 1;
	const start = (page - 1) * limit;
	return {
		data: rows.slice(start, start + limit),
		hasNextPage: start + limit < rows.length,
		currentPage: page,
		totalPages: Math.max(1, Math.ceil(rows.length / limit)),
		totalItemsCount: rows.length,
	};
}

async function scanDeletionRequests(
	params: ListGuestUsersParams,
): Promise<InfinityPage<GuestUserRow>> {
	const rows: GuestUserRow[] = [];
	let page = 1;
	let hasNextPage = true;
	while (hasNextPage && page <= SCAN_PAGES) {
		const chunk = await listGuestUsers({
			...safeParams(params),
			page,
			limit: SCAN_LIMIT,
			sort: "createdAt",
			order: "DESC",
		});
		rows.push(...chunk.data.filter((row) => row.deletionRequestedAt));
		hasNextPage = chunk.hasNextPage;
		page += 1;
	}
	const ordered =
		params.sort === "deletionRequested"
			? [...rows].sort(byDeletionRequested(params.order))
			: rows;
	return slicePage(ordered, params);
}

/**
 * Loads the guests table.
 *
 * Develop accepts `sort=createdAt|name` and ignores an unknown deletion filter,
 * and `sort=deletionRequested` is a 422 that would otherwise stick. A rejected
 * or unfiltered response is loaded again without those params and narrowed here.
 */
export async function loadGuestUsers(
	params: ListGuestUsersParams,
): Promise<InfinityPage<GuestUserRow>> {
	if (!params.deletionRequested && params.sort !== "deletionRequested") {
		return listGuestUsers(params);
	}

	try {
		const page = await listGuestUsers(params);
		if (!params.deletionRequested || serverHonouredFilter(page)) {
			return page;
		}
	} catch (error: unknown) {
		if (!rejectedByApi(error)) {
			throw error;
		}
	}

	if (params.deletionRequested) {
		return scanDeletionRequests(params);
	}

	const page = await listGuestUsers(safeParams(params));
	return {
		...page,
		data: [...page.data].sort(byDeletionRequested(params.order)),
	};
}
